import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import axios from 'axios';
import i18n from '../i18n';
import { toast } from 'sonner';
import { initPurchases } from '../lib/purchases';
import { markFreshAccount } from '../lib/onboarding';
import { storiesApi } from '../lib/api';
import { getAnonStory, clearAnonStory, setClaimedStoryId, pendingClaimCode } from '../lib/anonStory';
import { getPlatform } from '../lib/platform';
import type { UserResponse, TokenResponse } from '../lib/types';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface AuthContextValue {
  user: UserResponse | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<UserResponse>;
  register: (email: string, password: string, childName: string | null, childAge: number | null, language: string, website?: string) => Promise<UserResponse>;
  /** Native Google Sign-In: the Capacitor plugin's ID token → /auth/google. */
  loginWithGoogle: (idToken: string, opts?: { language?: string; childAge?: number | null }) => Promise<{ user: UserResponse; isNewUser: boolean }>;
  /** Native iOS Sign in with Apple: the plugin's identity token (JWT). */
  loginWithApple: (identityToken: string, opts?: { authorizationCode?: string | null; language?: string; childAge?: number | null }) => Promise<{ user: UserResponse; isNewUser: boolean }>;
  /** Web Google Sign-In: tokens handed over by /auth/callback's URL fragment. */
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<UserResponse>;
  logout: () => void;
  /** Re-fetch /auth/me (e.g. after the user verified their email in another tab). */
  refreshUser: () => Promise<UserResponse | null>;
  updateSettings: (settings: { child_name?: string | null; child_age?: number; language?: string }) => Promise<UserResponse>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function syncLanguage(userData: UserResponse) {
  const lang = userData.language || 'de';
  // Cast: react-i18next v16 + i18next v25 have a module-augmentation ordering
  // quirk where `i18n`'s methods aren't visible to TS at this call site.
  (i18n as unknown as { changeLanguage: (lng: string) => Promise<unknown> }).changeLanguage(lang);
  localStorage.setItem('language', lang);
}

const TOKEN_KEY = 'snugtale_token';
const REFRESH_KEY = 'snugtale_refreshToken';

function storeTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

/** The anonymous trial story's claim token, if this device created one. Sent
 *  with every register/login so the backend re-parents the story. */
function pendingClaimToken(): string | undefined {
  return getAnonStory()?.claim_token || undefined;
}

function noteClaim(data: TokenResponse) {
  if (data.claimed_story_id) {
    clearAnonStory();
    setClaimedStoryId(data.claimed_story_id);
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor for auth
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch current user on mount
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        syncLanguage(response.data);
        initPurchases(response.data.id);

        // A trial story created in another tab while already logged in:
        // claim it now. 404 = expired or claimed elsewhere → forget it.
        // 409 = this account already redeemed its one trial → forget it and say so.
        const leftover = getAnonStory();
        if (leftover) {
          // A record imported into the app by code (2026-09-26) has no token — claim it by its code.
          (leftover.claim_token ? storiesApi.claim(leftover.claim_token) : storiesApi.claimByCode(leftover.claim_code || ''))
            .then((r) => { clearAnonStory(); setClaimedStoryId(r.data.story_id); })
            .catch((err: { response?: { status?: number } }) => {
              const status = err.response?.status;
              if (status === 404 || status === 409) clearAnonStory();
              if (status === 409) {
                toast.info((i18n as unknown as { t: (k: string) => string }).t('anon.alreadyClaimed'));
              }
            });
        }
      } catch (error) {
        // The 401 interceptor in api.ts will try to refresh automatically.
        // If refresh also fails, tokens are cleared. Check if we still have a token.
        if (!localStorage.getItem(TOKEN_KEY)) {
          setToken(null);
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  // Shared tail of every successful auth call.
  const applyAuth = useCallback((data: TokenResponse): UserResponse => {
    storeTokens(data.access_token, data.refresh_token);
    setToken(data.access_token);
    setUser(data.user);
    syncLanguage(data.user);
    initPurchases(data.user.id);
    noteClaim(data);
    return data.user;
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<UserResponse> => {
    const response = await axios.post<TokenResponse>(`${API_URL}/auth/login`, {
      email, password, app_id: 'snugtale', claim_token: pendingClaimToken(), claim_code: pendingClaimCode(),
    });
    return applyAuth(response.data);
  }, [applyAuth]);

  const register = useCallback(async (email: string, password: string, childName: string | null, childAge: number | null, language: string, website = ''): Promise<UserResponse> => {
    const response = await axios.post<TokenResponse>(`${API_URL}/auth/register`, {
      email,
      password,
      child_name: childName,
      child_age: childAge,
      language,
      website,
      claim_token: pendingClaimToken(),
      claim_code: pendingClaimCode(),
      platform: getPlatform(),
    });
    markFreshAccount();
    return applyAuth(response.data);
  }, [applyAuth]);

  const loginWithGoogle = useCallback(async (idToken: string, opts: { language?: string; childAge?: number | null } = {}) => {
    const response = await axios.post<TokenResponse>(`${API_URL}/auth/google`, {
      id_token: idToken,
      app_id: 'snugtale',
      language: opts.language,
      child_age: opts.childAge ?? undefined,
      claim_token: pendingClaimToken(),
      claim_code: pendingClaimCode(),
      platform: getPlatform(),
    });
    if (response.data.is_new_user) markFreshAccount();
    const userData = applyAuth(response.data);
    return { user: userData, isNewUser: !!response.data.is_new_user };
  }, [applyAuth]);

  const loginWithApple = useCallback(async (identityToken: string, opts: { authorizationCode?: string | null; language?: string; childAge?: number | null } = {}) => {
    const response = await axios.post<TokenResponse>(`${API_URL}/auth/apple`, {
      identity_token: identityToken,
      authorization_code: opts.authorizationCode ?? undefined,
      app_id: 'snugtale',
      language: opts.language,
      child_age: opts.childAge ?? undefined,
      claim_token: pendingClaimToken(),
      claim_code: pendingClaimCode(),
    });
    if (response.data.is_new_user) markFreshAccount();
    const userData = applyAuth(response.data);
    return { user: userData, isNewUser: !!response.data.is_new_user };
  }, [applyAuth]);

  const refreshUser = useCallback(async (): Promise<UserResponse | null> => {
    try {
      const response = await axios.get<UserResponse>(`${API_URL}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch {
      return null;
    }
  }, []);

  const loginWithTokens = useCallback(async (accessToken: string, refreshToken: string): Promise<UserResponse> => {
    storeTokens(accessToken, refreshToken);
    // Set the header now rather than waiting for the token effect, so the
    // profile fetch below is authenticated on the first try.
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.get<UserResponse>(`${API_URL}/auth/me`);
    setToken(accessToken);
    setUser(response.data);
    syncLanguage(response.data);
    initPurchases(response.data.id);
    return response.data;
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setToken(null);
    setUser(null);
  }, []);

  const updateSettings = useCallback(async (settings: { child_name?: string | null; child_age?: number; language?: string }): Promise<UserResponse> => {
    const response = await axios.put(`${API_URL}/users/settings`, settings);
    setUser(response.data);
    if (settings.language) {
      syncLanguage(response.data);
    }
    return response.data;
  }, []);

  const value: AuthContextValue = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    loginWithApple,
    loginWithTokens,
    logout,
    refreshUser,
    updateSettings,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
